import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  TRACKER_CADENCES,
  TRACKER_CADENCE_LABELS,
  TRACKER_MAX_ACTIONS,
  TRACKER_TEMPLATES,
  type TrackerCadence,
  type TrackerMeta,
} from '@awwyeah/shared';
import { useUser } from '../auth';
import { TrackerCard } from './components/TrackerCard';
import { Sheet } from './components/Sheet';
import {
  addAction,
  createTrackerFromTemplate,
  removeAction,
  updateTracker,
  useTrackerRegistry,
} from './hooks';

/**
 * Trackers in the Apps tab (Dan, 2026-08-18).
 *
 * No bottom-nav slot of its own — same call as `GamesSection`, for the same reason: the
 * brief tolerates 0–6 nav entries and we're at the ceiling. `AppsIndexPage` imports this
 * from the module's published `index.ts`, the established cross-module seam.
 *
 * This is the management surface (create, rename, pin, edit buttons, remove); the *use*
 * surface is the card, which also renders on the digest for pinned trackers. Nothing here
 * shows a history, a count or a comparison between members — see the module contract in
 * `@awwyeah/shared`'s trackers docblock.
 */
export function TrackersSection(): JSX.Element {
  const { user } = useUser();
  const { map, trackers } = useTrackerRegistry();
  const [adding, setAdding] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);

  const menuTracker = trackers.find((tracker) => tracker.id === menuId) ?? null;

  if (!user) return <></>;

  return (
    <section className="flex flex-col gap-3" data-testid="trackers-section">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-fg">Trackers</h3>
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="new-tracker-button"
          className="flex h-9 items-center gap-1.5 rounded-full border border-line-strong px-3 text-[13px] font-semibold text-fg-2"
        >
          <Plus size={15} /> New
        </button>
      </div>

      {trackers.length === 0 ? (
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-line px-4 py-6">
          <p className="text-sm text-fg-2">
            A tracker answers &ldquo;has anyone fed the dog?&rdquo; — anyone taps, everyone sees
            when it last happened.
          </p>
          <button
            type="button"
            onClick={() => setAdding(true)}
            data-testid="trackers-empty-cta"
            className="mt-1 h-11 self-start rounded-md bg-inverse px-4 text-sm font-semibold text-inverse-fg"
          >
            Add one
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {trackers.map((tracker) => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              onOpenMenu={() => setMenuId(tracker.id)}
            />
          ))}
        </div>
      )}

      <Sheet
        open={adding}
        onClose={() => setAdding(false)}
        title="New tracker"
        tall
        testId="new-tracker-sheet"
      >
        <div className="flex flex-col gap-2 pb-2">
          <p className="text-sm text-fg-2">
            Start from one of these — you can rename it and change the buttons afterwards.
          </p>
          {TRACKER_TEMPLATES.map((template) => (
            <button
              key={template.name}
              type="button"
              data-testid="tracker-template"
              onClick={() => {
                createTrackerFromTemplate(map, template, user.id);
                setAdding(false);
              }}
              className="flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-3 text-left transition-colors hover:bg-raised"
            >
              <span className="text-[22px]" aria-hidden="true">
                {template.emoji}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-fg">{template.name}</span>
                <span className="block truncate text-xs text-fg-3">
                  {template.actions.map((action) => action.label).join(' · ')}
                </span>
              </span>
            </button>
          ))}
        </div>
      </Sheet>

      {menuTracker && (
        <TrackerSettingsSheet
          tracker={menuTracker}
          userId={user.id}
          onClose={() => setMenuId(null)}
          onChange={(patch) => updateTracker(map, menuTracker.id, patch, user.id)}
          onAddAction={(action) => addAction(map, menuTracker.id, action, user.id)}
          onRemoveAction={(actionId) => removeAction(map, menuTracker.id, actionId, user.id)}
        />
      )}
    </section>
  );
}

function TrackerSettingsSheet({
  tracker,
  userId: _userId,
  onClose,
  onChange,
  onAddAction,
  onRemoveAction,
}: {
  tracker: TrackerMeta;
  userId: string;
  onClose: () => void;
  onChange: (patch: { name?: string; pinned?: boolean; archived?: boolean }) => void;
  onAddAction: (action: { label: string; emoji?: string; cadence: TrackerCadence }) => void;
  onRemoveAction: (actionId: string) => void;
}): JSX.Element {
  const [name, setName] = useState(tracker.name);
  const [newLabel, setNewLabel] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [newCadence, setNewCadence] = useState<TrackerCadence>('daily');
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const atCap = tracker.actions.length >= TRACKER_MAX_ACTIONS;

  return (
    <Sheet open onClose={onClose} title={tracker.name} tall testId="tracker-settings-sheet">
      <div className="flex flex-col gap-5 pb-2">
        <div className="flex flex-col gap-1.5">
          <label
            className="text-xs font-semibold uppercase tracking-wide text-fg-3"
            htmlFor="tracker-name"
          >
            Name
          </label>
          <input
            id="tracker-name"
            data-testid="tracker-name-input"
            className="h-12 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name !== tracker.name && onChange({ name: name.trim() })}
          />
        </div>

        <label className="flex items-center justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-fg">Show on the digest</span>
            <span className="block text-xs text-fg-3">
              Puts this card on the morning screen, under today&apos;s agenda.
            </span>
          </span>
          <input
            type="checkbox"
            data-testid="tracker-pin-toggle"
            checked={tracker.pinned}
            onChange={(e) => onChange({ pinned: e.target.checked })}
            className="h-6 w-6 shrink-0 accent-[var(--app-accent)]"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">Buttons</span>
          {tracker.actions.map((action) => (
            <div
              key={action.id}
              data-testid="tracker-action-row"
              className="flex items-center gap-2 rounded-sm border border-line px-3 py-2"
            >
              <span aria-hidden="true">{action.emoji ?? '•'}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-fg">{action.label}</span>
                <span className="block text-xs text-fg-3">
                  {TRACKER_CADENCE_LABELS[action.cadence]}
                </span>
              </span>
              <button
                type="button"
                data-testid="remove-tracker-action"
                aria-label={`Remove ${action.label}`}
                onClick={() => onRemoveAction(action.id)}
                className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-danger"
              >
                Remove
              </button>
            </div>
          ))}

          {atCap ? (
            <p className="text-xs text-fg-3">
              That&apos;s {TRACKER_MAX_ACTIONS} buttons — as many as fit under a thumb.
            </p>
          ) : (
            <div className="flex flex-col gap-2 rounded-sm border border-dashed border-line p-3">
              <div className="flex gap-2">
                <input
                  aria-label="Button emoji"
                  data-testid="new-action-emoji"
                  className="h-11 w-14 rounded border-2 border-line-strong bg-bg px-2 text-center text-base"
                  placeholder="🦴"
                  value={newEmoji}
                  onChange={(e) => setNewEmoji(e.target.value)}
                />
                <input
                  aria-label="Button label"
                  data-testid="new-action-label"
                  className="h-11 flex-1 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
                  placeholder="Brushed"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                />
              </div>
              <select
                aria-label="How often"
                data-testid="new-action-cadence"
                className="h-11 rounded border-2 border-line-strong bg-bg px-2 text-sm text-fg"
                value={newCadence}
                onChange={(e) => setNewCadence(e.target.value as TrackerCadence)}
              >
                {TRACKER_CADENCES.map((cadence) => (
                  <option key={cadence} value={cadence}>
                    {TRACKER_CADENCE_LABELS[cadence]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                data-testid="add-tracker-action"
                disabled={!newLabel.trim()}
                onClick={() => {
                  onAddAction({
                    label: newLabel.trim(),
                    ...(newEmoji.trim() ? { emoji: newEmoji.trim() } : {}),
                    cadence: newCadence,
                  });
                  setNewLabel('');
                  setNewEmoji('');
                }}
                className="h-11 rounded-md bg-inverse text-sm font-semibold text-inverse-fg disabled:opacity-50"
              >
                Add button
              </button>
            </div>
          )}
        </div>

        {/* Archive, not delete: the tap log is a record of things that actually happened,
            and a tracker put away is recoverable in a way a deleted doc isn't. */}
        {confirmingRemove ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-fg-2">
              Put &ldquo;{tracker.name}&rdquo; away? It stops showing up; nothing already logged is
              lost.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="confirm-archive-tracker"
                onClick={() => {
                  onChange({ archived: true });
                  onClose();
                }}
                className="h-11 flex-1 rounded-md bg-danger text-sm font-semibold text-surface"
              >
                Put it away
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRemove(false)}
                className="h-11 flex-1 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
              >
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-testid="archive-tracker"
            onClick={() => setConfirmingRemove(true)}
            className="h-11 self-start text-sm font-semibold text-danger"
          >
            Put this tracker away
          </button>
        )}
      </div>
    </Sheet>
  );
}
